import React from "react";
import * as moment from "moment-timezone";
import { useSelector } from "react-redux";

import { RootState } from "../../../../store";
import { notification } from "../../../../utils/Notifications.tsx";
import { findRecordsForReport } from "../../../../services/assistanceCheck.service.ts";
import { ReporteChequeo } from "../../../../models/ReporteChequeo.ts";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import AccordionCheck from "./AccordionCheck.tsx";
import { logsCheckModalUi as S } from "./logsCheckModalUi.ts";

const LogsCheckModal = () => {
  const { currentState } = useSelector((state: RootState) => state.reportClock);
  const [records, setRecords] = React.useState<ReporteChequeo[]>([]);
  const [loading, setLoading] = React.useState(true);

  const fechtRecords = async () => {
    setLoading(true);
    try {
      const { day, partner, branch } = currentState;
      const end = moment.utc(day).add(1, "day").format("YYYY-MM-DD");

      const records = await findRecordsForReport(branch, partner, day, end);

      const STANDARD_CUTOFF_MINUTES = 3 * 60;

      /** Mismo día calendario del día operativo: entradas desde las 03:00. */
      const today = records.filter((record: ReporteChequeo) => {
        const calKey = moment
          .utc(record.assistanceCheck_checkDate)
          .format("YYYY-MM-DD");
        if (calKey !== day) {
          return false;
        }
        const [hours, minutes, seconds] = record.assistanceCheck_checkTime
          .split(":")
          .map(Number);
        const totalMinutes = hours * 60 + minutes + seconds / 60;
        return totalMinutes >= STANDARD_CUTOFF_MINUTES;
      });

      /** Madrugada del día siguiente: salida del turno vespertino/nocturno. */
      const tomorrow = records.filter((record: ReporteChequeo) => {
        const calKey = moment
          .utc(record.assistanceCheck_checkDate)
          .format("YYYY-MM-DD");
        if (calKey !== end) {
          return false;
        }
        const [hours, minutes, seconds] = record.assistanceCheck_checkTime
          .split(":")
          .map(Number);
        const totalMinutes = hours * 60 + minutes + seconds / 60;
        return totalMinutes < STANDARD_CUTOFF_MINUTES;
      });

      const info = [...today, ...tomorrow].sort(
        (a, b) => a.assistanceCheck_id - b.assistanceCheck_id
      );

      setRecords(info);
    } catch {
      notification("Error ⚠️", "Error al obtener los registros", "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fechtRecords();
  }, [currentState.partner, currentState.branch, currentState.day]);

  const first = records[0];
  const personName = first
    ? toCamelCase(
        `${first.person_firstName} ${first.person_lastName} ${first.person_secondLastName}`
      )
    : "";
  const dayLabel = currentState.day
    ? moment.utc(currentState.day, "YYYY-MM-DD").format("DD [de] MMMM YYYY")
    : "";

  return (
    <div>
      {!loading && records.length > 0 && (
        <div style={S.summary}>
          <div>
            {personName && <p style={S.summaryName}>{personName}</p>}
            <p style={S.summaryMeta}>
              {dayLabel}
              {first?.branch_name ? ` · ${first.branch_name}` : ""}
            </p>
          </div>
          <span style={S.countBadge}>
            <i className="fa-solid fa-clock" aria-hidden />
            {records.length}{" "}
            {records.length === 1 ? "registro" : "registros"}
          </span>
        </div>
      )}

      {loading && (
        <div style={S.loadingWrap}>
          <i
            className="fa-solid fa-spinner fa-spin fa-2x mb-3"
            style={{ color: "#50B7C0" }}
            aria-hidden
          />
          <p className="mb-0">Cargando registros...</p>
        </div>
      )}

      {!loading && records.length === 0 && (
        <div style={S.emptyWrap}>
          <i
            className="fa-regular fa-clock fa-3x mb-3"
            style={{ color: "#cbd5e1" }}
            aria-hidden
          />
          <h5 className="mb-1" style={{ color: "#334155", fontWeight: 600 }}>
            Sin registros
          </h5>
          <p className="mb-0 small">
            No hay checadas para este día y colaborador.
          </p>
        </div>
      )}

      {!loading && records.length > 0 && (
        <div style={S.list}>
          {records.map((record) => (
            <AccordionCheck
              key={record.assistanceCheck_id}
              check={record}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LogsCheckModal;
